import * as React from 'react';

import { Edit } from '@mui/icons-material';
import { Box, Button, Chip, Typography } from '@mui/material';

/**
 * The products a set of distances was measured from.
 *
 * One component for both places anchors are read — the saved norm set and the
 * translations hanging off it — so the same products are drawn the same way
 * whichever page an analyst reaches them from. They used to be chips on one and
 * a two-column table on the other, which made the same fact look like two.
 *
 * `onChangeAnchors` is what makes them editable. It is a quiet outlined button
 * rather than a bare link: on the translation page it is one of only two things
 * the page offers, and a text button on its own line read as a footnote.
 */

type Props = {
  anchors: string[];
  /** why these products are here, in the words of the page asking */
  caption: React.ReactNode;
  onChangeAnchors?: () => void;
};

const AnchorList: React.FC<Props> = ({ anchors, caption, onChangeAnchors }) => (
  <Box>
    <Typography variant="subtitle1">Anchors</Typography>
    <Typography
      variant="caption"
      sx={{ display: 'block', mb: 1, color: 'text.secondary' }}
    >
      {caption}
    </Typography>

    {anchors.length > 0 ? (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {anchors.map((a) => (
          <Chip key={a} size="small" variant="outlined" label={a} />
        ))}
      </Box>
    ) : (
      <Typography variant="caption" sx={{ color: 'warning.main' }}>
        None recorded, so nothing here was measured against anything.
      </Typography>
    )}

    {onChangeAnchors && (
      <Box sx={{ mt: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Edit fontSize="small" />}
          onClick={onChangeAnchors}
          sx={{ textTransform: 'none' }}
        >
          Change anchors
        </Button>
      </Box>
    )}
  </Box>
);

export default AnchorList;
